import type { PixelCrop } from 'react-image-crop';

/** Rotate an image URL by 90° clockwise and return a new JPEG data URL. */
export async function rotateImage90(src: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      // 90° CW: new width = old height, new height = old width
      canvas.width = img.naturalHeight;
      canvas.height = img.naturalWidth;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('No canvas context')); return; }
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.crossOrigin = 'anonymous';
    img.src = src;
  });
}

/** Apply a pixel crop (from react-image-crop) to an <img> element and return a File. */
export async function cropImageFromElement(
  imgEl: HTMLImageElement,
  crop: PixelCrop,
  fileName: string,
  mimeType: string,
): Promise<File> {
  const scaleX = imgEl.naturalWidth / imgEl.width;
  const scaleY = imgEl.naturalHeight / imgEl.height;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(crop.width * scaleX);
  canvas.height = Math.round(crop.height * scaleY);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No canvas context');

  ctx.drawImage(
    imgEl,
    Math.round(crop.x * scaleX),
    Math.round(crop.y * scaleY),
    canvas.width,
    canvas.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) { reject(new Error('Canvas export failed')); return; }
        resolve(new File([blob], fileName, { type: mimeType }));
      },
      mimeType,
      0.92,
    );
  });
}

/** Convert a URL (blob URL or data URL) to a File, for the "skip crop" path. */
export async function urlToFile(src: string, fileName: string, mimeType: string): Promise<File> {
  const res = await fetch(src);
  const blob = await res.blob();
  return new File([blob], fileName, { type: mimeType });
}
